/**
 * Progression recognition — "Which chord progression did you hear?"
 *
 * Plays a 4-bar progression. The user identifies it by its Roman numeral
 * pattern. Distractors are other common progressions.
 *
 * Cadence recognition — "What cadence does this end on?"
 *
 * Plays a 4-chord progression ending on the target cadence type.
 */

import { AudioPlanBuilder, type AudioPlan } from "../audio-plan.js";
import type { Exercise, AnswerOption } from "../exercise.js";
import { Rng } from "../exercise.js";
import {
  buildChord,
  degreeOffset,
  DIATONIC_TRIAD_QUALITIES,
  pickKey,
  smoothVoicing,
  type KeyChoice,
} from "../utils.js";

const PROGRESSION_PATTERNS: Record<string, number[]> = {
  "I-IV-V-I":   [1, 4, 5, 1],
  "I-V-vi-IV":  [1, 5, 6, 4],
  "ii-V-I":     [2, 5, 1],
  "I-vi-IV-V":  [1, 6, 4, 5],
  "vi-IV-I-V":  [6, 4, 1, 5],
};

const PROGRESSION_LABELS: Record<string, string> = {
  "I-IV-V-I":  "I – IV – V – I",
  "I-V-vi-IV": "I – V – vi – IV",
  "ii-V-I":    "ii – V – I",
  "I-vi-IV-V": "I – vi – IV – V",
  "vi-IV-I-V": "vi – IV – I – V",
};

function playProgressionChords(
  builder: AudioPlanBuilder,
  key: KeyChoice,
  degrees: number[],
  opts: {
    qualityOverrides?: Record<number, string>;
    chordDur?: number;
    gap?: number;
  } = {},
): void {
  const qualities = DIATONIC_TRIAD_QUALITIES[key.mode];
  let prevVoicing: number[] | null = null;
  const chordDur = opts.chordDur ?? 0.95;
  const gap = opts.gap ?? 0.05;
  const qualityOverrides = opts.qualityOverrides ?? {};
  for (const deg of degrees) {
    const quality = qualityOverrides[deg] ?? qualities[deg - 1]!;
    const rootMidi = key.tonicMidi + degreeOffset(deg, key.mode);
    const triad = buildChord(rootMidi, quality);
    const voicing = smoothVoicing(prevVoicing, triad);
    prevVoicing = voicing;
    builder.addNote(rootMidi - 12, chordDur, 0.11, "bass");
    builder.addChord(voicing, chordDur, 0.07, "harmony");
    builder.advance(chordDur + gap);
  }
}

/**
 * Wrong-answer teach plan for progressions and cadences: replay the
 * same chord sequence at roughly half tempo with a longer breath
 * between chords, so the harmonic rhythm becomes audible. The chord
 * voicings are recomputed so smoothVoicing gives the same line.
 */
function buildProgressionTeachPlan(
  key: KeyChoice,
  degrees: number[],
  qualityOverrides: Record<number, string> = {},
): AudioPlan {
  const builder = new AudioPlanBuilder();
  playProgressionChords(builder, key, degrees, {
    qualityOverrides,
    chordDur: 1.6,
    gap: 0.2,
  });
  builder.advance(0.5);
  return builder.build();
}

/* ─── Progression recognition ─────────────────────────────────────────────── */

export interface ProgressionParams {
  progression: string;
}

export function generateProgression(
  itemId: string,
  params: ProgressionParams,
  seed: number,
): Exercise {
  const rng = new Rng(seed);
  const key = pickKey(rng, "major");
  const degrees = PROGRESSION_PATTERNS[params.progression];
  if (!degrees) throw new Error(`Unknown progression: ${params.progression}`);

  const builder = new AudioPlanBuilder();
  playProgressionChords(builder, key, degrees);
  builder.advance(0.5);
  const audio = builder.build();

  const allKeys = Object.keys(PROGRESSION_PATTERNS);
  const distractors = allKeys.filter((k) => k !== params.progression);
  const optionKeys = rng.shuffle([params.progression, ...rng.sample(distractors, 3)]);

  const options: AnswerOption[] = optionKeys.map((k) => ({
    id: k,
    label: PROGRESSION_LABELS[k] ?? k,
  }));

  return {
    itemId,
    exerciseType: "progression-recognition",
    audio,
    prompt: "Which progression did you hear?",
    options,
    correctAnswerId: params.progression,
    meta: {
      keyName: key.name,
      seed,
      explanation: `${PROGRESSION_LABELS[params.progression]} in ${key.name}.`,
      distractorCount: options.length - 1,
      teachAudio: buildProgressionTeachPlan(key, degrees),
    },
  };
}

/* ─── Cadence recognition ────────────────────────────────────────────────── */

const CADENCE_PROGRESSIONS: Record<string, number[]> = {
  PAC:       [1, 4, 5, 1],
  IAC:       [1, 4, 5, 1], // same chords, different inversions/voicing handled below
  plagal:    [1, 5, 4, 1],
  half:      [1, 6, 4, 5],
  deceptive: [1, 4, 5, 6],
};
const CADENCE_LABELS: Record<string, string> = {
  PAC: "Perfect authentic (V–I)",
  IAC: "Imperfect authentic (V–I, weaker)",
  plagal: "Plagal (IV–I)",
  half: "Half (ends on V)",
  deceptive: "Deceptive (V–vi)",
};

export interface CadenceParams {
  cadence: string;
}

export function generateCadence(
  itemId: string,
  params: CadenceParams,
  seed: number,
): Exercise {
  const rng = new Rng(seed);
  const key = pickKey(rng, "major");
  const degrees = CADENCE_PROGRESSIONS[params.cadence];
  if (!degrees) throw new Error(`Unknown cadence: ${params.cadence}`);

  const builder = new AudioPlanBuilder();
  playProgressionChords(builder, key, degrees);
  builder.advance(0.5);
  const audio = builder.build();

  const allKeys = Object.keys(CADENCE_PROGRESSIONS);
  const distractors = allKeys.filter((k) => k !== params.cadence);
  const optionKeys = rng.shuffle([params.cadence, ...rng.sample(distractors, 3)]);

  const options: AnswerOption[] = optionKeys.map((k) => ({
    id: k,
    label: CADENCE_LABELS[k] ?? k,
  }));

  return {
    itemId,
    exerciseType: "cadence-recognition",
    audio,
    prompt: "What cadence does this progression end on?",
    options,
    correctAnswerId: params.cadence,
    meta: {
      keyName: key.name,
      seed,
      explanation: `${CADENCE_LABELS[params.cadence]} in ${key.name}.`,
      distractorCount: options.length - 1,
      teachAudio: buildProgressionTeachPlan(key, degrees),
    },
  };
}
